// Shipping Address Service
// Manages multiple customer shipping addresses

const ADDRESSES_KEY = 'shipping_addresses';
const DEFAULT_ADDRESS_KEY = 'default_shipping_address';

export const addressService = {
  // Get all saved addresses for customer
  getAddresses: () => {
    try {
      const addresses = localStorage.getItem(ADDRESSES_KEY);
      return addresses ? JSON.parse(addresses) : [];
    } catch (error) {
      console.error('Error loading addresses:', error);
      return [];
    }
  },

  // Get default address
  getDefaultAddress: () => {
    try {
      const addresses = addressService.getAddresses();
      const defaultId = localStorage.getItem(DEFAULT_ADDRESS_KEY);
      
      // Find address marked as default
      const defaultAddress = addresses.find(addr => addr.id === defaultId) || 
                            addresses.find(addr => addr.isDefault) ||
                            addresses[0]; // First address as fallback
      
      return defaultAddress || null;
    } catch (error) {
      console.error('Error loading default address:', error);
      return null;
    }
  },

  // Save new address
  saveAddress: (addressData) => {
    try {
      const addresses = addressService.getAddresses();
      const newAddress = {
        ...addressData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // If this is the first address or marked as default, set as default
      if (addresses.length === 0 || addressData.isDefault) {
        // Clear other default addresses
        addresses.forEach(addr => addr.isDefault = false);
        newAddress.isDefault = true;
        localStorage.setItem(DEFAULT_ADDRESS_KEY, newAddress.id);
      }

      addresses.push(newAddress);
      localStorage.setItem(ADDRESSES_KEY, JSON.stringify(addresses));
      
      return { success: true, address: newAddress };
    } catch (error) {
      console.error('Error saving address:', error);
      return { success: false, error: error.message };
    }
  },

  // Update existing address
  updateAddress: (addressId, addressData) => {
    try {
      const addresses = addressService.getAddresses();
      const addressIndex = addresses.findIndex(addr => addr.id === addressId);
      
      if (addressIndex === -1) {
        return { success: false, error: 'Address not found' };
      }

      addresses[addressIndex] = {
        ...addresses[addressIndex],
        ...addressData,
        updatedAt: new Date().toISOString()
      };

      // Handle default address changes
      if (addressData.isDefault) {
        addresses.forEach((addr, idx) => {
          addr.isDefault = idx === addressIndex;
        });
        localStorage.setItem(DEFAULT_ADDRESS_KEY, addressId);
      }

      localStorage.setItem(ADDRESSES_KEY, JSON.stringify(addresses));
      
      return { success: true, address: addresses[addressIndex] };
    } catch (error) {
      console.error('Error updating address:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete address
  deleteAddress: (addressId) => {
    try {
      const addresses = addressService.getAddresses();
      const filteredAddresses = addresses.filter(addr => addr.id !== addressId);
      
      // If deleted address was default, make first remaining address default
      const deletedAddress = addresses.find(addr => addr.id === addressId);
      if (deletedAddress?.isDefault && filteredAddresses.length > 0) {
        filteredAddresses[0].isDefault = true;
        localStorage.setItem(DEFAULT_ADDRESS_KEY, filteredAddresses[0].id);
      } else if (filteredAddresses.length === 0) {
        localStorage.removeItem(DEFAULT_ADDRESS_KEY);
      }

      localStorage.setItem(ADDRESSES_KEY, JSON.stringify(filteredAddresses));
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting address:', error);
      return { success: false, error: error.message };
    }
  },

  // Set default address
  setDefaultAddress: (addressId) => {
    try {
      const addresses = addressService.getAddresses();
      addresses.forEach(addr => {
        addr.isDefault = addr.id === addressId;
      });

      localStorage.setItem(ADDRESSES_KEY, JSON.stringify(addresses));
      localStorage.setItem(DEFAULT_ADDRESS_KEY, addressId);
      
      return { success: true };
    } catch (error) {
      console.error('Error setting default address:', error);
      return { success: false, error: error.message };
    }
  }
};
